import React, {CSSProperties, MouseEvent, MouseEventHandler} from 'react';
import classNames from "classnames";
import Modal from "@mui/joy/Modal";


import {IconProps} from "../../../components/icons";


import {InfoIcon} from "../../../components/icons/InfoIcon";
import {DangerIcon} from "../../../components/icons/DangerIcon";
import {SuccessIcon} from "../../../components/icons/SuccessIcon";
import {CloseIcon} from "../../../components/icons/CloseIcon";
import {WarningIcon} from "../../../components/icons/WarningIcon";


import {screenSizeProvider} from "@stores/ScreenSizeProvider";


import './ModalBase.css';


export type ModalConfirmType = 'info' | 'warning' | 'success' | 'neutral' | 'danger';

export interface ModalBaseProps {
    open: boolean;
    type?: ModalConfirmType
    header?: string | null | React.ReactNode;
    onClose?: () => void;
    children: React.ReactNode;
    minWidth?: CSSProperties['minWidth'];
    maxWidth?: CSSProperties['maxWidth'];
    maxHeight?: number;
    isDark?: boolean;
    isMobileFullScreen?: boolean;
    isMobileBottomSheet?: boolean;
    isHeaderSticky?: boolean;
    hideBackdrop?: boolean;
    onClick?: MouseEventHandler;
    subtitle?: string | React.ReactNode;
    contentClassName?: string
    headerClass?: string;
}


export const ModalBase = React.forwardRef<HTMLDivElement, ModalBaseProps>((
    {
        open = false,
        type = undefined,
        header,
        onClose,
        onClick,
        children,
        minWidth = 400,
        maxWidth = undefined,
        maxHeight = 826,
        headerClass = 'title-large-bold',
        isDark = false,
        isMobileFullScreen = false,
        isMobileBottomSheet = false,
        isHeaderSticky = false,
        hideBackdrop = false,
        subtitle,
        contentClassName
    }: ModalBaseProps,
    ref
) => {
    const isMobile = screenSizeProvider.view !== 'desktop';
    const isBottomSheet = isMobile && isMobileBottomSheet && !isMobileFullScreen;
    const Icon = getIconComponent(type!);

    const handleModalClick = (event: MouseEvent) => {
        // to prevent accidental ctx menu close on mobile (schedule setting exception modal example)
        if (isMobile) {
            event.stopPropagation();
        }
    }

    const renderHeader = () => (
        !isMobileFullScreen
            ? (
                <div className={'ModalBase_header'}>
                    <div className={`ModalBase_title ${headerClass}`}>
                        {Icon && <Icon className="ModalTitle__icon" size={24}/>}
                        {header}
                    </div>
                    {subtitle &&
                        <div className="body-medium ModalBase_subtitle">
                            {subtitle}
                        </div>
                    }
                </div>
            )
            : (
                <div className={classNames({
                    'MobileFullScreenHeader title-medium-bold': true,
                    'MobileFullScreenHeader--sticky': isHeaderSticky,
                    'theme-dark': isDark
                })}>
                    <div className="MobileFullScreenHeader__titleGroup">
                        {Icon ? <Icon size={24}/> : null}
                        {header}
                    </div>
                    <CloseIcon
                        className={'MobileFullScreenHeader__icon'}
                        size={24}
                        additionalPadding={12}
                        onClick={onClose}
                    />
                </div>
            )
    );

    return (
        <Modal
            open={open}
            onClick={onClick}
            onClose={onClose}
            sx={{
                display: 'flex',
                alignItems: isBottomSheet ? 'flex-end' : 'center',
                justifyContent: 'center',
                zIndex: 4000,
                maxHeight: '100dvh',
            }}
            hideBackdrop={hideBackdrop}
        >
            <div
                className={
                    classNames({
                        'ModalBase': true,
                        'ModalBase--info': type === 'info',
                        'ModalBase--neutral': type === 'neutral',
                        'ModalBase--danger': type === 'danger',
                        'ModalBase--success': type === 'success',
                        'ModalBase--warning': type === 'warning',
                        'ModalBaseFullScreen': isMobileFullScreen,
                        'ModalBaseFullScreen--stickyHeader': isMobileFullScreen && isHeaderSticky,
                        'ModalBase--bottomSheet': isBottomSheet,
                        'theme-dark': isDark,
                    })}
                ref={ref}
                tabIndex={-1}
                style={{
                    minWidth: isMobile ? undefined : minWidth,
                    maxWidth: isBottomSheet ? undefined : maxWidth,
                    maxHeight: `min(${maxHeight}px, 100dvh)`,
                    ...(isMobileFullScreen && isHeaderSticky ? {overflow: 'hidden'} : {}),
                }}
                onClick={handleModalClick}
            >
                {header && renderHeader()}
                <div className={classNames({
                    'ModalBase_content body-large': true,
                    'ModalBase_content--no-header': !header,
                    [contentClassName!]: contentClassName,
                })}>
                    {children}
                </div>
                {
                    !isMobileFullScreen && onClose ? (
                        <div
                            className='ModalBase_close'
                        >
                            <CloseIcon
                                className="modalClose_button"
                                additionalPadding={8}
                                size={16}
                                onClick={onClose}/>
                        </div>
                    ) : null
                }
            </div>
        </Modal>
    )
})

const getIconComponent = (type?: ModalConfirmType): React.ComponentType<IconProps> | undefined => {
    switch (type) {
        case 'info':
            return InfoIcon;
        case 'warning':
            return WarningIcon;
        case 'danger':
            return DangerIcon;
        case 'success':
            return SuccessIcon;
        default:
            return undefined;
    }
}

